import React from 'react';
import { Download, X, Calculator, Info } from 'lucide-react';
import { createPortal } from 'react-dom';
import api from '../../../../../services/api';
import CommercialsEditor from '../../../team-lead/analysis/components/Commercials/CommercialsEditor';

// ─── Cost Estimation / Download Modal ─────────────────────────────────────────
const CostEstimationModal = ({ rfpId, editableData, fetchRFPData, onClose }) => {
    const [isDownloadingEstimation, setIsDownloadingEstimation] = React.useState(false);
    const [estimationError, setEstimationError] = React.useState(null);
    const [fetchedEstimation, setFetchedEstimation] = React.useState(null);
    const [isFetchingEstimation, setIsFetchingEstimation] = React.useState(false);

    React.useEffect(() => {
        if (!rfpId) return;
        const fetchCostEstimation = async () => {
            try {
                setIsFetchingEstimation(true);
                setEstimationError(null);
                const response = await api.get(`/extraction/v1/${rfpId}/commercials/cost-estimation`);
                setFetchedEstimation(response.data);
            } catch (err) {
                console.error('Failed to fetch cost estimation:', err);
                setEstimationError(err.response?.data?.message || err.message || 'Failed to fetch cost estimation.');
            } finally {
                setIsFetchingEstimation(false);
            }
        };
        fetchCostEstimation();
    }, [rfpId]);

    const handleDownloadEstimation = async () => {
        try {
            setIsDownloadingEstimation(true);
            setEstimationError(null);
            const response = await api.get(`/extraction/v1/${rfpId}/commercials/cost-estimation/download`, {
                headers: { 'Accept': 'application/pdf' },
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Cost_Estimation_Pricing_${rfpId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download cost estimation', err);
            if (err.response?.data instanceof Blob) {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const parsed = JSON.parse(reader.result);
                        setEstimationError(parsed.detail || parsed.message || 'Failed to download cost estimation.');
                    } catch (e) {
                        setEstimationError(reader.result || 'Failed to download cost estimation.');
                    }
                };
                reader.readAsText(err.response.data);
            } else {
                setEstimationError(err.response?.data?.message || err.message || 'Failed to download cost estimation.');
            }
        } finally {
            setIsDownloadingEstimation(false);
        }
    };

    return createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(6px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div style={{ width: '90%', maxWidth: '1100px', height: '90%', backgroundColor: '#FFFFFF', borderRadius: '20px', boxShadow: '0 20px 60px rgba(15,23,42,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'ce-fade-in 0.22s cubic-bezier(0.16, 1, 0.3, 1)' }}>
                {/* Modal Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 32px', height: '76px', borderBottom: '1px solid #EEF2F7', backgroundColor: '#FFFFFF', flexShrink: 0 }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0F172A', letterSpacing: '-0.02em' }}>Cost Estimation &amp; Pricing</h3>
                        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#94A3B8', fontWeight: '400' }}>Resource-based commercial model</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button onClick={handleDownloadEstimation} disabled={isDownloadingEstimation} style={{ background: '#FF5526', color: '#fff', border: 'none', cursor: 'pointer', padding: '10px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '700', transition: 'all 0.2s', opacity: isDownloadingEstimation ? 0.7 : 1, boxShadow: '0 4px 14px rgba(255, 85, 38, 0.25)' }} onMouseEnter={(e) => { if (!isDownloadingEstimation) { e.currentTarget.style.background = '#1b5cc2'; e.currentTarget.style.transform = 'translateY(-1px)'; } }} onMouseLeave={(e) => { e.currentTarget.style.background = '#FF5526'; e.currentTarget.style.transform = 'none'; }}>
                            <Download size={15} />
                            <span>{isDownloadingEstimation ? 'Downloading...' : 'Download PDF'}</span>
                        </button>
                        <button onClick={onClose} style={{ background: '#F1F5F9', border: '1px solid #E2E8F0', cursor: 'pointer', color: '#64748B', width: '38px', height: '38px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.background = '#E2E8F0'; e.currentTarget.style.color = '#0F172A'; }} onMouseLeave={(e) => { e.currentTarget.style.background = '#F1F5F9'; e.currentTarget.style.color = '#64748B'; }}>
                            <X size={18} />
                        </button>
                    </div>
                </div>
                {/* Modal Body */}
                <div className="ce-scroll-area" style={{ flex: 1, overflowY: 'auto', padding: '12px 20px', background: '#F8F9FA', position: 'relative' }}>
                    {isFetchingEstimation ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '6rem 0', gap: '1.25rem' }}>
                            <div className="animate-spin" style={{ width: '40px', height: '40px', border: '4px solid #E2E8F0', borderTopColor: '#FF5526', borderRadius: '50%' }} />
                            <span style={{ fontSize: '14px', color: '#64748B', fontWeight: '500' }}>Fetching Cost Estimation...</span>
                        </div>
                    ) : (
                        <>
                            {estimationError && (
                                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '14px', padding: '14px 20px', marginBottom: '24px', color: '#991B1B', fontSize: '13.5px', fontWeight: '500', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ fontSize: '18px' }}>⚠️</span>
                                        <span>{estimationError}</span>
                                    </div>
                                    <button onClick={() => setEstimationError(null)} style={{ background: 'none', border: 'none', color: '#991B1B', cursor: 'pointer', fontWeight: '700', fontSize: '18px', padding: '0 4px', lineHeight: 1 }}>✕</button>
                                </div>
                            )}
                            <CommercialsEditor
                                rfpId={rfpId}
                                commercialsData={fetchedEstimation || editableData?.commercials_estimation || editableData?.commercials}
                                onSaveSuccess={() => {
                                    if (typeof fetchRFPData === 'function') fetchRFPData(true);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

// ─── RACI Legend Component ─────────────────────────────────────────────────────
export const RaciLegend = () => {
    const items = [
        { letter: 'R', label: 'Responsible', bg: '#FFF4ED', color: '#E9471D', dotBg: '#E9471D' },
        { letter: 'A', label: 'Accountable', bg: '#FFF1EC', color: '#D63F15', dotBg: '#FF8866' },
        { letter: 'C', label: 'Consulted', bg: '#ECFDF5', color: '#047857', dotBg: '#34D399' },
        { letter: 'I', label: 'Informed', bg: '#F8F9FA', color: '#475569', dotBg: '#CBD5E1' },
    ];
    return (
        <div className="raci-legend-container" style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', padding: 'auto' }}>
                <span>RACI Legend</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                {items.map(({ letter, label, bg, color, dotBg }) => (
                    <div key={letter} style={{ display: 'inline-flex', alignItems: 'center', height: '34px', borderRadius: '9999px', padding: '0 12px', fontSize: '13px', fontWeight: '500', gap: '8px', backgroundColor: bg, color }}>
                        <span style={{ width: '22px', height: '22px', borderRadius: '50%', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: dotBg, color: '#FFFFFF' }}>{letter}</span>
                        <span>{label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Export Button Component ───────────────────────────────────────────────────
export const CommercialsSectionButton = ({ rfpId, editableData, fetchRFPData }) => {
    const [show, setShow] = React.useState(false);
    return (
        <>
            <button
                className="section-export-inline-btn"
                onClick={() => setShow(true)}
                title="Open commercials cost estimation"
                style={{ background: '#FF5526', color: '#FFFFFF', border: 'none', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', transition: 'all 0.2s ease', fontWeight: '700' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1b5cc2'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#FF5526'}
            >
                <Calculator size={14} style={{ color: '#FFFFFF' }} />
                <span style={{ color: '#FFFFFF' }}>Estimation</span>
            </button>
            {show && <CostEstimationModal rfpId={rfpId} editableData={editableData} fetchRFPData={fetchRFPData} onClose={() => setShow(false)} />}
        </>
    );
};

// ─── Auto-save info banner ─────────────────────────────────────────────────────
export const AutoSaveBanner = () => (
    <div className="editor-autosave-banner" aria-label="Auto-save notification">
        <Info size={14} className="autosave-banner-icon" />
        <span className="autosave-banner-text">Changes are saved automatically and will also be preserved when you exit.</span>
    </div>
);
